// apps/web/src/features/cargo/components/CargoRegistryPage.tsx
//
// Page entry for `/cargo` — the Cargo registry (#1231). Dense wide table,
// always-visible article search, collapsible filters (collapsed by default),
// role-aware columns/controls, and cursor pagination (50/page). A `view` mode
// (#1888) toggles between the `list` table and a `statistics` body (KPI cards +
// chart sections over the current filters); the mode lives in a separate `view`
// search param so filter writes never clobber it.

import { Suspense, lazy, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAuthStore } from '../../../stores/auth.js';
import { type CargoArticleWire } from '@magimp/shared';

import {
  Button,
  FilterPanel,
  FilterToggleButton,
  Icon,
  Input,
  ToolbarActions,
  useFilterDisclosure,
} from '../../../ui/index.js';
import {
  canSeeCargoLogisticColumns,
  canSeeCargoStatistics,
  usesCargoDefaultDateWindow,
} from '../role.js';
import {
  useCargoAssignableManagers,
  useCargoFilterOptions,
  useCargoListInfinite,
  useCargoStatistics,
} from '../queries.js';
import { CARGO_SEARCH_MAX_CHARS, useCargoFilters } from '../useCargoFilters.js';
import { useCargoWorkflows } from '../useCargoWorkflows.js';
import { visibleCargoColumns } from './cargoColumns.js';
import { buildCargoActiveFilterChips, CargoActiveFilters } from './CargoActiveFilters.js';
import { CargoFiltersPanel } from './CargoFiltersPanel.js';
import { CargoRegistryTable } from './CargoRegistryTable.js';
import { CargoModalHost } from './CargoModalHost.js';

// WHY: lazy-load the statistics body so recharts (pulled in via the ui/charts
// primitives) stays out of the main bundle — it loads only when an allowed role
// opens `?view=statistics` (#2165). The app has no route-level code splitting,
// so a local Suspense boundary is the contained way to keep recharts off the
// list path.
const CargoStatisticsView = lazy(() =>
  import('./CargoStatisticsView.js').then((m) => ({ default: m.CargoStatisticsView })),
);

export function CargoRegistryPage() {
  const { t, i18n } = useTranslation(['cargo']);
  const user = useAuthStore((s) => s.user);

  const showLogisticColumns = canSeeCargoLogisticColumns(user);
  // Statistics mode is gated to a fixed role cohort; a denied user (manager) is
  // forced to list mode and never fires the statistics fetch (FR-10).
  const canSeeStatistics = canSeeCargoStatistics(user);

  // WHY: `view` lives in its OWN useSearchParams read, NOT in the cargo filter
  // param set — a filter write must never clear `view`, and a view toggle must
  // never touch the filter params. Unknown/invalid values fall back to `list`.
  const [viewParams, setViewParams] = useSearchParams();
  const rawView = viewParams.get('view');
  const view = canSeeStatistics && rawView === 'statistics' ? 'statistics' : 'list';
  const setView = (next: 'list' | 'statistics') => {
    setViewParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'statistics') out.set('view', 'statistics');
        else out.delete('view');
        return out;
      },
      { replace: true },
    );
  };

  const filters = useCargoFilters({
    applyDefaultDateWindow: usesCargoDefaultDateWindow(user),
  });

  const listQuery = useCargoListInfinite(filters.wireFilters);

  // WHY: readOnlyMirror comes from the list envelope (Part A), so the hook
  // call must follow listQuery. Both hooks are unconditional — reordering
  // two always-called hooks is safe under the Rules of Hooks.
  const firstPage = listQuery.data?.pages[0];
  // Mirror state is UNKNOWN until the first /cargo envelope resolves. Fail-safe:
  // gate every write control/modal as if read-only until we know otherwise, so a
  // slow first fetch never briefly exposes a write affordance (#1635). The banner
  // stays keyed to the ACTUAL resolved value so it does not flash during load.
  const mirrorResolved = firstPage !== undefined;
  const readOnlyMirror = firstPage?.readOnlyMirror ?? false;
  const writesBlocked = !mirrorResolved || readOnlyMirror;
  const workflows = useCargoWorkflows(writesBlocked);
  const showImport = workflows.canImport;
  const showAdd = workflows.canAdd;
  // The backend strips `responsibleManagerId` itself (D-5), so the scoped
  // wire filters can be passed directly. Enabled always — the active-filter
  // manager chip needs the name even while the filters panel is closed.
  const filterOptionsQuery = useCargoFilterOptions(filters.wireFilters);
  // #2261: the Add/Edit assignment select is fed from the users directory (all
  // active manager-role users), NOT the row-derived filter-options — so a
  // newly-registered manager with zero rows is assignable. Gated on write
  // ability so a read-only actor never fires the `cargo.update` endpoint (403).
  const assignableManagersQuery = useCargoAssignableManagers({
    enabled: workflows.canAdd || workflows.canEditRows,
  });

  const filtersDisclosure = useFilterDisclosure();

  // Statistics fetch only fires in statistics mode for an allowed role — never
  // in list mode, and never for a manager (FR-10).
  const statisticsQuery = useCargoStatistics(filters.wireFilters, {
    enabled: view === 'statistics' && canSeeStatistics,
  });

  const rows = useMemo<readonly CargoArticleWire[]>(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data?.pages],
  );
  // On loading/error fall back to an empty list (safe state) — never throw,
  // never re-derive from loaded rows.
  const managerOptions = useMemo(
    () => filterOptionsQuery.data?.managers.map((m) => ({ id: m.id, name: m.name })) ?? [],
    [filterOptionsQuery.data],
  );
  const pathFormatOptions = useMemo(
    () => filterOptionsQuery.data?.pathFormats ?? [],
    [filterOptionsQuery.data],
  );
  // Assignment select source (Add/Edit modals). Empty until the roster resolves
  // (safe state) — the Edit modal re-appends a stored-but-unassignable manager
  // locally (FR-5), so a still-loading roster never drops the current value.
  const assignableManagerOptions = useMemo(
    () => assignableManagersQuery.data?.managers.map((m) => ({ id: m.id, name: m.name })) ?? [],
    [assignableManagersQuery.data],
  );
  const activeFilterCount = buildCargoActiveFilterChips(filters, managerOptions, t).length;
  const columns = useMemo(() => visibleCargoColumns(showLogisticColumns), [showLogisticColumns]);

  // FR-7: a quick preset writes the `china_ship` date window through the
  // existing date setters (which stamp `datesTouched`), so the statistics query
  // refetches over the new range.
  const applyDatePreset = (from: string, to: string) => {
    filters.setDateType('china_ship');
    filters.setDateFrom(from);
    filters.setDateTo(to);
  };

  return (
    <div
      data-testid="cargo-registry-page"
      style={{
        position: 'relative',
        margin: -16,
        height: 'calc(100% + 32px)',
        width: 'calc(100% + 32px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-0)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 24px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-1)',
          flex: 'none',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--fz-22)',
            fontWeight: 600,
            color: 'var(--fg-1)',
            letterSpacing: '-.005em',
            flex: '0 0 auto',
          }}
        >
          {t('cargo:title')}
        </h1>

        <ToolbarActions style={{ gap: 12, flex: '1 1 0', justifyContent: 'flex-end' }}>
          <div
            data-testid="cargo-search-input"
            style={{ flex: '0 1 360px', minWidth: 0, maxWidth: 360 }}
          >
            <Input
              icon="search"
              ariaLabel={t('cargo:list.searchPlaceholder')}
              placeholder={t('cargo:list.searchPlaceholder')}
              value={filters.q}
              onChange={filters.setQ}
              maxLength={CARGO_SEARCH_MAX_CHARS}
            />
          </div>
          <FilterToggleButton
            open={filtersDisclosure.open}
            activeCount={activeFilterCount}
            label={t('cargo:actions.filters')}
            size="md"
            aria-controls={filtersDisclosure.panelId}
            onClick={filtersDisclosure.toggle}
            data-testid="cargo-panels-toggle"
          />
          {canSeeStatistics && (
            <Button
              kind="ghost"
              icon="bar-chart-3"
              active={view === 'statistics'}
              aria-pressed={view === 'statistics'}
              data-testid="cargo-statistics-toggle"
              onClick={() => setView(view === 'statistics' ? 'list' : 'statistics')}
            >
              {t('cargo:statistics.toggle')}
            </Button>
          )}
          {showImport && (
            <Button
              kind="secondary"
              icon="upload-cloud"
              data-testid="cargo-import-button"
              onClick={workflows.openImport}
            >
              {t('cargo:actions.import')}
            </Button>
          )}
          {showAdd && (
            <Button
              kind="primary"
              icon="plus"
              data-testid="cargo-add-button"
              onClick={workflows.openAdd}
            >
              {t('cargo:actions.add')}
            </Button>
          )}
        </ToolbarActions>
      </div>

      {/* WHY: no shared Banner primitive exists in apps/web/src/ui, so this
          strip is locally built with CSS tokens per apps/web/CLAUDE.md. */}
      {readOnlyMirror && (
        <div
          role="status"
          data-testid="cargo-readonly-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 24px',
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-fg)',
            fontSize: 'var(--fz-13)',
            flex: 'none',
            borderBottom: '1px solid var(--border-1)',
          }}
        >
          <Icon name="alert-circle" size={14} />
          {t('cargo:mirror.banner')}
        </div>
      )}

      <CargoActiveFilters filters={filters} managerOptions={managerOptions} />

      <FilterPanel
        open={filtersDisclosure.open}
        id={filtersDisclosure.panelId}
        // FilterPanel's base chrome is suppressed here: CargoFiltersPanel below
        // already owns the panel's padding/border/background frame, so the shared
        // wrapper stays bare to avoid double padding + a double bottom border.
        style={{ flex: 'none', padding: 0, background: 'transparent', borderBottom: 'none' }}
      >
        <div data-testid="cargo-panels">
          <CargoFiltersPanel
            filters={filters}
            managerOptions={managerOptions}
            pathFormatOptions={pathFormatOptions}
          />
        </div>
      </FilterPanel>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: 'var(--bg-0)' }}>
        {view === 'statistics' ? (
          <Suspense
            fallback={
              <p data-testid="cargo-stats-loading" style={stateTextStyle}>
                {t('cargo:statistics.loading')}
              </p>
            }
          >
            <CargoStatisticsView
              stats={statisticsQuery.data}
              isLoading={statisticsQuery.isLoading}
              isError={statisticsQuery.isError}
              onApplyDatePreset={applyDatePreset}
            />
          </Suspense>
        ) : listQuery.isLoading ? (
          <p data-testid="cargo-loading" style={stateTextStyle}>
            {t('cargo:loading')}
          </p>
        ) : listQuery.isError ? (
          <p data-testid="cargo-error" role="alert" style={errorTextStyle}>
            {t('cargo:error')}
          </p>
        ) : rows.length === 0 ? (
          <div data-testid="cargo-empty" style={{ ...stateTextStyle, display: 'flex', gap: 8 }}>
            <Icon name="package-open" size={16} />
            {t('cargo:empty')}
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <CargoRegistryTable
                rows={rows}
                columns={columns}
                locale={i18n.language}
                actions={workflows.rowActions}
                onRowClick={workflows.canOpenRows ? workflows.onRowClick : undefined}
              />
            </div>
            {listQuery.hasNextPage && (
              <div
                style={{
                  padding: '12px 24px',
                  display: 'flex',
                  justifyContent: 'center',
                  borderTop: '1px solid var(--border-1)',
                  flex: 'none',
                }}
              >
                <Button
                  kind="secondary"
                  size="sm"
                  data-testid="cargo-load-more"
                  disabled={listQuery.isFetchingNextPage}
                  onClick={() => {
                    void listQuery.fetchNextPage();
                  }}
                >
                  {listQuery.isFetchingNextPage
                    ? t('cargo:list.loadingMore')
                    : t('cargo:list.loadMore')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <CargoModalHost workflows={workflows} managerOptions={assignableManagerOptions} />
    </div>
  );
}

const stateTextStyle = {
  padding: 24,
  color: 'var(--fg-3)',
  fontSize: 'var(--fz-13)',
} as const;

const errorTextStyle = {
  ...stateTextStyle,
  color: 'var(--status-error-fg)',
} as const;
